import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Card, Col, Form, Nav, Pagination, Row, Spinner, Table } from "react-bootstrap";
import axios from "axios";
import { TranslatorContext } from "../../context/Translator";
import PageLayout from "../../layouts/PageLayout";

const PAGE_SIZE = 20;

const getAuthConfig = () => ({
    headers: {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        Accept: "application/json",
    },
});

const getPagedResult = (response, page) => {
    const paginator = response.data?.data;
    return {
        rows: Array.isArray(paginator?.data) ? paginator.data : [],
        pagination: {
            currentPage: paginator?.current_page || page,
            lastPage: paginator?.last_page || 1,
            total: paginator?.total || 0,
            from: paginator?.from || 0,
            to: paginator?.to || 0,
            perPage: paginator?.per_page || PAGE_SIZE,
        },
    };
};

const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? "-"
        : date.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

const formatAmount = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount)
        ? amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : "0.00";
};

const getPaymentStatus = (payment) => {
    const status = String(payment.status || payment.payment_type || "").trim();
    return status ? status.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase()) : "-";
};

const getPaymentStatusVariant = (payment) => {
    const status = String(payment.status || payment.payment_type || "").toLowerCase();
    if (status === "credit" || status === "received" || status === "paid") return "success";
    if (status === "debit") return "primary";
    return "secondary";
};

function LedgerPagination({ pagination, onPageChange, label }) {
    if (pagination.lastPage <= 1) return null;

    const pages = [];
    const firstVisible = Math.max(1, pagination.currentPage - 1);
    const lastVisible = Math.min(pagination.lastPage, pagination.currentPage + 1);

    pages.push(
        <Pagination.Prev
            key="previous"
            disabled={pagination.currentPage <= 1}
            onClick={() => onPageChange(pagination.currentPage - 1)}
        />
    );
    if (firstVisible > 1) {
        pages.push(<Pagination.Item key={1} onClick={() => onPageChange(1)}>1</Pagination.Item>);
        if (firstVisible > 2) pages.push(<Pagination.Ellipsis key="left-ellipsis" disabled />);
    }
    for (let page = firstVisible; page <= lastVisible; page += 1) {
        pages.push(
            <Pagination.Item
                key={page}
                active={page === pagination.currentPage}
                onClick={() => onPageChange(page)}
            >
                {page}
            </Pagination.Item>
        );
    }
    if (lastVisible < pagination.lastPage) {
        if (lastVisible < pagination.lastPage - 1) {
            pages.push(<Pagination.Ellipsis key="right-ellipsis" disabled />);
        }
        pages.push(
            <Pagination.Item
                key={pagination.lastPage}
                onClick={() => onPageChange(pagination.lastPage)}
            >
                {pagination.lastPage}
            </Pagination.Item>
        );
    }
    pages.push(
        <Pagination.Next
            key="next"
            disabled={pagination.currentPage >= pagination.lastPage}
            onClick={() => onPageChange(pagination.currentPage + 1)}
        />
    );

    return (
        <Card.Footer className="bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
            <small className="text-muted">
                {label}: {pagination.from || 0}-{pagination.to || 0} of {pagination.total}
            </small>
            <Pagination size="sm" className="mb-0">{pages}</Pagination>
        </Card.Footer>
    );
}

export default function ListerLedger({ entityName = "Lister", apiPrefix = "lister" }) {
    // Issuers are paid for products handed over to a lister; listers and
    // checkers for products a checker approved.
    const isIssuer = apiPrefix === "issuer";
    const productsLabel = isIssuer ? "Products handed over" : "Approved products";
    const { t } = useContext(TranslatorContext);
    const [activeTab, setActiveTab] = useState("summary");
    const [summary, setSummary] = useState(null);
    const [products, setProducts] = useState([]);
    const [payments, setPayments] = useState([]);
    const [productsPage, setProductsPage] = useState(1);
    const [paymentsPage, setPaymentsPage] = useState(1);
    const [productsPagination, setProductsPagination] = useState({
        currentPage: 1, lastPage: 1, total: 0, from: 0, to: 0, perPage: PAGE_SIZE,
    });
    const [paymentsPagination, setPaymentsPagination] = useState({
        currentPage: 1, lastPage: 1, total: 0, from: 0, to: 0, perPage: PAGE_SIZE,
    });
    const [productSearch, setProductSearch] = useState("");
    const [paymentSearch, setPaymentSearch] = useState("");
    const [paymentFromDate, setPaymentFromDate] = useState("");
    const [paymentToDate, setPaymentToDate] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const loadSummary = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/${apiPrefix}/ledger/summary`,
                getAuthConfig()
            );
            if (!response.data?.success) {
                throw new Error(response.data?.message || "Failed to load ledger summary.");
            }
            setSummary(response.data?.data || {});
        } catch (err) {
            console.error(`Error fetching ${apiPrefix} ledger summary:`, err);
            setError(err.response?.data?.message || err.message || "Error fetching ledger summary.");
        } finally {
            setLoading(false);
        }
    }, [apiPrefix]);

    const loadProducts = useCallback(async (page) => {
        setLoading(true);
        setError("");
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/${apiPrefix}/ledger/approved-products`,
                { ...getAuthConfig(), params: { page, per_page: PAGE_SIZE } }
            );
            if (!response.data?.success) {
                throw new Error(response.data?.message || "Failed to load approved products.");
            }
            const result = getPagedResult(response, page);
            setProducts(result.rows);
            setProductsPagination(result.pagination);
        } catch (err) {
            console.error(`Error fetching ${apiPrefix} approved products:`, err);
            setError(err.response?.data?.message || err.message || "Error fetching approved products.");
            setProducts([]);
        } finally {
            setLoading(false);
        }
    }, [apiPrefix]);

    const loadPayments = useCallback(async (page) => {
        setLoading(true);
        setError("");
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/${apiPrefix}/ledger/payments`,
                { ...getAuthConfig(), params: { page, per_page: PAGE_SIZE } }
            );
            if (!response.data?.success) {
                throw new Error(response.data?.message || "Failed to load payment history.");
            }
            const result = getPagedResult(response, page);
            setPayments(result.rows);
            setPaymentsPagination(result.pagination);
        } catch (err) {
            console.error(`Error fetching ${apiPrefix} ledger payments:`, err);
            setError(err.response?.data?.message || err.message || "Error fetching payment history.");
            setPayments([]);
        } finally {
            setLoading(false);
        }
    }, [apiPrefix]);

    useEffect(() => {
        if (activeTab === "summary") loadSummary();
        if (activeTab === "products") loadProducts(productsPage);
        if (activeTab === "payments") loadPayments(paymentsPage);
    }, [activeTab, productsPage, paymentsPage, loadSummary, loadProducts, loadPayments]);

    const settlementType = (summary?.settlement_type || "").toLowerCase();
    const userPays = summary?.payment_direction === "receive" || settlementType === "receivable";
    const userReceives = summary?.payment_direction === "pay" || settlementType === "payable";

    const summaryLabels = userPays
        ? {
            total: "Total amount you owe",
            settled: "Already paid",
            remaining: "Still to pay",
            extra: "Extra amount you paid",
        }
        : userReceives
            ? {
                total: "Total amount owed to you",
                settled: "Already received",
                remaining: "Still to receive",
                extra: "Extra amount received",
            }
            : {
                total: "Total amount",
                settled: "Settled amount",
                remaining: "Remaining amount",
                extra: "Extra amount",
            };

    const visibleProducts = useMemo(() => {
        const keyword = productSearch.trim().toLowerCase();
        if (!keyword) return products;
        return products.filter((product) =>
            [product.product_code, product.title]
                .some((value) => String(value || "").toLowerCase().includes(keyword))
        );
    }, [products, productSearch]);

    const visiblePayments = useMemo(() => {
        const keyword = paymentSearch.trim().toLowerCase();
        return payments.filter((payment) => {
            const matchesText = !keyword || [
                payment.payment_type,
                payment.bank?.name,
                payment.creator?.name,
                payment.notes,
            ].some((value) => String(value || "").toLowerCase().includes(keyword));
            const paymentDate = payment.payment_date ? String(payment.payment_date).slice(0, 10) : "";
            return matchesText
                && (!paymentFromDate || paymentDate >= paymentFromDate)
                && (!paymentToDate || paymentDate <= paymentToDate);
        });
    }, [payments, paymentSearch, paymentFromDate, paymentToDate]);

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Body>
                            <h3 className="fw-bold mb-1">{t(`${entityName} Ledger`)}</h3>
                            <p className="text-muted mb-0">
                                {t(isIssuer ? "See the products you handed over, payment progress, and payment history." : "See your approved products, payment progress, and payment history.")}
                            </p>
                        </Card.Body>
                    </Card>
                    {error && <Alert variant="danger">{error}</Alert>}

                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom-0 pt-3">
                            <Nav
                                variant="tabs"
                                activeKey={activeTab}
                                onSelect={(tab) => tab && setActiveTab(tab)}
                            >
                                <Nav.Item><Nav.Link eventKey="summary">{t("Summary")}</Nav.Link></Nav.Item>
                                <Nav.Item><Nav.Link eventKey="products">{t(isIssuer ? "Handed Over Products" : "Approved Products")}</Nav.Link></Nav.Item>
                                <Nav.Item><Nav.Link eventKey="payments">{t("Payments")}</Nav.Link></Nav.Item>
                            </Nav>
                        </Card.Header>

                        {loading ? (
                            <Card.Body className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <div className="small text-muted mt-2">{t("Loading ledger...")}</div>
                            </Card.Body>
                        ) : (
                            <>
                                {activeTab === "summary" && (
                                    <Card.Body className="pt-4">
                                        {summary ? (
                                            <Row className="justify-content-center">
                                                <Col lg={8} xl={7}>
                                                    <Card className="border shadow-sm">
                                                        <Card.Body className="p-0">
                                                            <div className={`p-4 border-bottom ${userPays ? "bg-warning-subtle" : userReceives ? "bg-success-subtle" : "bg-light"}`}>

                                                                <h4 className={`fw-bold mb-1 ${userPays ? "text-warning-emphasis" : userReceives ? "text-success" : "text-dark"}`}>
                                                                    {userPays
                                                                        ? t("You need to pay")
                                                                        : userReceives
                                                                            ? t("You will receive")
                                                                            : t("Your account balance")}
                                                                </h4>

                                                            </div>
                                                            <div className="px-4">
                                                                {[
                                                                    { label: summaryLabels.total, value: summary.total_amount, emphasized: true },
                                                                    { label: summaryLabels.settled, value: summary.settled_amount },
                                                                    { label: summaryLabels.remaining, value: summary.remaining_amount, emphasized: true },
                                                                    { label: summaryLabels.extra, value: summary.extra_amount },
                                                                    { label: "Opening balance", value: summary.opening_balance },
                                                                    { label: "Payment method", value: summary.payment_type ? t(summary.payment_type.replaceAll("_", " ")) : "-", text: true },
                                                                    { label: "Rate / fixed payment amount", value: summary.payment_value },
                                                                    { label: productsLabel, value: summary.approved_products, count: true },
                                                                ].map((item, index, list) => (
                                                                    <div
                                                                        key={item.label}
                                                                        className={`d-flex justify-content-between align-items-center gap-3 py-3 ${index < list.length - 1 ? "border-bottom" : ""}`}
                                                                    >
                                                                        <span className={item.emphasized ? "fw-semibold text-dark" : "text-muted"}>
                                                                            {t(item.label)}
                                                                        </span>
                                                                        <span className={`text-end ${item.emphasized ? "fw-bold text-dark fs-5" : "fw-semibold text-dark"}`}>
                                                                            {item.count
                                                                                ? item.value ?? 0
                                                                                : item.text
                                                                                    ? item.value
                                                                                    : `PKR ${formatAmount(item.value)}`}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            </Row>
                                        ) : (
                                            <div className="text-center text-muted py-4">{t("No summary data available.")}</div>
                                        )}
                                    </Card.Body>
                                )}

                                {activeTab === "products" && (
                                    <>
                                        <Card.Body className="pb-3">
                                            <Row className="g-3 align-items-end">
                                                <Col md={6} lg={6}>
                                                    <Form.Group>
                                                        <Form.Label className="small fw-semibold text-muted">{t("Search this page")}</Form.Label>
                                                        <Form.Control
                                                            type="search"
                                                            value={productSearch}
                                                            onChange={(event) => setProductSearch(event.target.value)}
                                                            placeholder={t("Product title or code")}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                        <div className="table-responsive">
                                            <Table hover className="mb-0 align-middle">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>{t("Product")}</th>
                                                        <th>{t("Product Code")}</th>
                                                        <th>{t(isIssuer ? "Handed Over" : "Approved Date")}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {visibleProducts.length ? visibleProducts.map((product) => (
                                                        <tr key={product.id}>
                                                            <td>
                                                                <div className="d-flex align-items-center gap-3">
                                                                    {product.card_img ? (
                                                                        <img
                                                                            src={`${process.env.REACT_APP_IMG_URL}/${product.card_img}`}
                                                                            alt={product.title || t("Product image")}
                                                                            className="rounded border object-fit-cover flex-shrink-0"
                                                                            style={{ width: 48, height: 48 }}
                                                                        />
                                                                    ) : (
                                                                        <div
                                                                            className="bg-light rounded border d-flex align-items-center justify-content-center text-muted small flex-shrink-0"
                                                                            style={{ width: 48, height: 48 }}
                                                                        >
                                                                            {t("No image")}
                                                                        </div>
                                                                    )}
                                                                    <span className="fw-semibold">{product.title || "-"}</span>
                                                                </div>
                                                            </td>
                                                            <td>{product.product_code || "-"}</td>
                                                            <td>{formatDate(product.approved_at)}</td>
                                                        </tr>
                                                    )) : (
                                                        <tr>
                                                            <td colSpan={4} className="text-center text-muted py-4">
                                                                {t("No approved products found on this page.")}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                        <LedgerPagination
                                            pagination={productsPagination}
                                            onPageChange={setProductsPage}
                                            label={t("Approved products")}
                                        />
                                    </>
                                )}

                                {activeTab === "payments" && (
                                    <>
                                        <Card.Body className="pb-3">
                                            <Row className="g-3">
                                                <Col md={6} lg={4}>
                                                    <Form.Group>
                                                        <Form.Label className="small fw-semibold text-muted">{t("Search this page")}</Form.Label>
                                                        <Form.Control
                                                            type="search"
                                                            value={paymentSearch}
                                                            onChange={(event) => setPaymentSearch(event.target.value)}
                                                            placeholder={t("Type, bank, creator, or notes")}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col sm={6} md={3}>
                                                    <Form.Group>
                                                        <Form.Label className="small fw-semibold text-muted">{t("From Date")}</Form.Label>
                                                        <Form.Control type="date" value={paymentFromDate} onChange={(event) => setPaymentFromDate(event.target.value)} />
                                                    </Form.Group>
                                                </Col>
                                                <Col sm={6} md={3}>
                                                    <Form.Group>
                                                        <Form.Label className="small fw-semibold text-muted">{t("To Date")}</Form.Label>
                                                        <Form.Control type="date" value={paymentToDate} onChange={(event) => setPaymentToDate(event.target.value)} />
                                                    </Form.Group>
                                                </Col>
                                                <Col xs={12} className="small text-muted">
                                                    {t("Filters apply to the records on this page.")}
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                        <div className="table-responsive">
                                            <Table hover className="mb-0 align-middle">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>{t("Payment Date")}</th>
                                                        <th>{t("Status")}</th>
                                                        <th>{t("Amount")}</th>
                                                        <th>{t("Bank")}</th>
                                                        <th>{t("Recorded By")}</th>
                                                        <th>{t("Notes")}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {visiblePayments.length ? visiblePayments.map((payment) => (
                                                        <tr key={payment.id}>
                                                            <td>{formatDate(payment.payment_date)}</td>
                                                            <td>
                                                                <Badge bg={getPaymentStatusVariant(payment)}>
                                                                    {t(getPaymentStatus(payment))}
                                                                </Badge>
                                                            </td>
                                                            <td className="fw-semibold">PKR {formatAmount(payment.amount)}</td>
                                                            <td>{payment.bank?.name || "-"}</td>
                                                            <td>{payment.creator?.name || "-"}</td>
                                                            <td>{payment.notes || "-"}</td>
                                                        </tr>
                                                    )) : (
                                                        <tr>
                                                            <td colSpan={6} className="text-center text-muted py-4">
                                                                {t("No payments found on this page.")}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                        <LedgerPagination
                                            pagination={paymentsPagination}
                                            onPageChange={setPaymentsPage}
                                            label={t("Payments")}
                                        />
                                    </>
                                )}
                            </>
                        )}
                    </Card>
                </Col>
            </Row>
        </PageLayout>
    );
}
